import React, { ReactElement } from 'react';
import { Feature, InjectorService } from '@tencent/workbench';
import BaseDrawerFeature from '@framework/workbench/common/feature/base-drawer-feature';
import { alphaToOpacity } from '@framework/workbench/drawer/utils';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { RADIAL_GRADIENT } from '@framework/workbench/drawer/ui/text/fill/config';
import { GradientType } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import {
    RECT_GRADIENT,
    SHAPE_GRADIENT,
} from '@framework/workbench/drawer/ui/fill/constants';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import ColorPickerContainer from '@src/js/common/ark-extensions/dc-components/color-picker/color-picker-container';
import { ColorPickerType } from '@src/js/common/ark-extensions/dc-components/color-picker/type';
import {
    IDirectionOption,
    IBorderShower,
} from '@tencent/drawing-drawer-components';
import { BorderFeature } from '@framework/workbench/drawer/ui/border/border-feature';
import { ILineEnd } from '@tencent/alloy-renderer-adapter';

const DEFAULT_WHITE_COLOR = '#000000';

@Feature()
export class BorderShower extends BaseDrawerFeature implements IBorderShower {
    private borderFeature: BorderFeature;

    public constructor() {
        super();
        this.borderFeature = InjectorService.get(BorderFeature);
    }

    public getCurrentBorderType = () => this.borderFeature.curBorderType;

    public getBorderWidth = () => {
        const widthObj = { width: this.borderFeature.width };
        return this.getShownValue(this.borderFeature.getInputParams().key, widthObj);
    };

    public getBorderStyle = () => this.borderFeature.style;

    public getBorderHeadEnd = () => this.borderInfo?.headEnd as ILineEnd;

    public getBorderTailEnd = () => this.borderInfo?.tailEnd as ILineEnd;

    public getBorderCompoundType = () => this.borderInfo?.compoundType;

    public getBorderOpacity = () => {
        const opacityObj = { opacity: this.borderFeature.opacity };
        return this.getShownValue(this.borderFeature.getInputParams().key, opacityObj);
    };

    public getBorderSketchType = () => this.borderInfo?.sketchStyle?.type;

    public getCurrentGradualType = () => this.borderFeature.borderGradualValues.curGradualType;

    public getGradualFillAngle = () => {
        const curAngleObj = { curAngle: this.borderFeature.borderGradualValues.curAngle! };
        return this.getShownValue(this.borderFeature.getInputParams().key, curAngleObj);
    };

    public getGradualPath = () => {
        const { curGradualType } = this.borderFeature.borderGradualValues;
        let path = GradientType.Linear;
        if (curGradualType === RADIAL_GRADIENT) {
            path = GradientType.Radial;
        } else if (curGradualType === RECT_GRADIENT) {
            path = GradientType.Rect;
        } else if (curGradualType === SHAPE_GRADIENT) {
            path = GradientType.Shape;
        }
        return path;
    };

    public getCurrentDirection = () => this.borderFeature.borderGradualValues.curDirect as IDirectionOption;

    public getGradualActivePointId = () => this.borderFeature.borderGradualValues.activePointId as string;

    public getPointArray = () => this.borderFeature.getPointArray();

    public getFillDirectionOptions = () => this.borderFeature.getFillDirectionOptions();

    public getColorPickerContainer = (): ReactElement => <ColorPickerContainer
        colorPickerType={ColorPickerType.StandardPC}
        onColorChange={this.borderFeature.handlePaletteGradualColorChange}
        selectedTdColor={this.borderFeature.borderGradualValues.activeColor}
    />;

    public getGradualFillOpacity = () => {
        const color = this.borderFeature.borderGradualValues.activeColor;
        if (!color) {
            return 0;
        }
        const gradualOpacityObj = { gradualOpacity: alphaToOpacity(color.alpha) };
        return this.getShownValue(this.borderFeature.getInputParams().key, gradualOpacityObj);
    };

    public shouldAngleDisabled(): boolean {
        return this.borderFeature.shouldAngleDisabled();
    }

    public getSolidFillColor = () => this.borderFeature.borderColor.rgba || DEFAULT_WHITE_COLOR;

    public getDropContent = (): ReactElement => <ColorPickerContainer
        onColorHover={this.borderFeature.handleColorEnter}
        onMouseLeave={this.borderFeature.handlePaletteLeave}
        selectedTdColor={this.borderFeature.borderColor || new TDColor({
            rgbaStr: DEFAULT_WHITE_COLOR,
        })}
        colorPickerType={ColorPickerType.StandardPC}
        onColorChange={this.borderFeature.changeBorderColor}
    />;

    public shouldEyeDropperDisabled = () => !this.getColorPickerManager()?.useNewEyeDropper;

    private get borderInfo() {
        return (this.getSelectModel() as IMultiplyModel | IElementBaseModel).getBorder?.()
            .getBorderInfo();
    }
}
